import React from 'react';
import { Shield, CheckCircle, FileText, Lock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import TrustedInstitutions from '../components/TrustedInstitutions';

const ServicioPLD = () => {
  const funcionalidades = [
    'Identificación y validación de clientes mediante listas oficiales (ONU, SAT, UIF, ENTRE OTROS).',
    'Clasificación de operaciones de acuerdo con niveles de riesgo.',
    'Monitoreo y alertas de transacciones inusuales o relevantes.',
    'Generación automática de reportes y avisos para autoridades competentes.',
    'Resguardo digital seguro de expedientes y documentación.',
    'Capacitación y auditoría interna para mantener actualizado a nuestro equipo.'
  ];

  const ventajas = [
    'Seguridad jurídica en cada operación.',
    'Transparencia y confianza en la información.',
    'Procesos automatizados gracias a la eficiencia y rapidez.',
    'Reducción de riesgos legales y fiscales.'
  ];

  const hitchSteps = [
    {
      title: 'Notario crea el Hitch',
      description: 'El notario registra el documento legal y designa a los firmantes (comprador y vendedor) con su nombre y correo electrónico.'
    },
    {
      title: 'Notificación por correo',
      description: 'Cada firmante recibe un correo electrónico con un enlace único e intransferible para reclamar su participación en el documento.'
    },
    {
      title: 'Registro en la plataforma',
      description: 'El firmante crea una cuenta en HITChain con su correo y contraseña, vinculando su identidad digital al documento.'
    },
    {
      title: 'Firma digital interna + Blockchain',
      description: 'Una vez autenticado, el firmante revisa el documento y ejecuta su firma digital dentro de la plataforma, generando un hash criptográfico único. El sistema genera automáticamente una transacción de attestation en Ethereum o Hedera Mainnet, registrando el hash de la firma en la cadena de bloques.'
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-notaria-primary to-notaria-secondary text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in">
              Servicio de prevención e identificación de operaciones vulnerables
            </h1>
            <p className="text-xl mb-8 text-gray-100 animate-fade-in">
              Manejamos sus números con precisión, brindándole información clara para impulsar movimientos comerciales confiables.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-notaria-primary mb-6 text-center">NOTARÍA 90</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed mb-12">
              <p className="text-lg">
                En nuestra <strong>Notaría</strong>, estamos comprometidos con la transparencia, la legalidad y la seguridad de las operaciones de nuestros clientes. Por ello, hemos implementado <strong>sistemas especializados de Prevención de Lavado de Dinero (PLD)</strong> que garantizan el cumplimiento de las disposiciones establecidas en la <strong>Ley Federal para la Prevención e Identificación de Operaciones con Recursos de Procedencia Ilícita (LFPIORPI)</strong>.
              </p>
            </div>

            {/* Funcionalidades */}
            <div className="mb-16">
              <h3 className="text-2xl md:text-3xl font-bold text-notaria-primary mb-8">Funcionalidades de nuestros sistemas de PLD</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {funcionalidades.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-notaria-secondary flex-shrink-0 mt-1" />
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What it means */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-notaria-primary mb-12 text-center">¿Qué significa esto para nuestros clientes?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="border-2 border-gray-200 hover:border-notaria-secondary transition-all duration-300">
                <CardContent className="p-6">
                  <Shield className="w-12 h-12 text-notaria-secondary mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Protección y confianza</h4>
                  <p className="text-gray-600">Todas las operaciones se llevan a cabo bajo un estricto control de seguridad y legalidad.</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200 hover:border-notaria-secondary transition-all duration-300">
                <CardContent className="p-6">
                  <FileText className="w-12 h-12 text-notaria-secondary mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Cumplimiento normativo</h4>
                  <p className="text-gray-600">Cumplimos con las obligaciones de identificación, resguardo de información y envío de reportes a la Unidad de Inteligencia Financiera (UIF).</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-200 hover:border-notaria-secondary transition-all duration-300">
                <CardContent className="p-6">
                  <Lock className="w-12 h-12 text-notaria-secondary mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Prevención de riesgos</h4>
                  <p className="text-gray-600">Evitamos que nuestra Notaría pueda ser utilizada de manera indebida en actividades ilícitas.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Ventajas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-notaria-primary mb-8 text-center">Ventajas para quienes nos eligen</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ventajas.map((ventaja, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-notaria-secondary flex-shrink-0" />
                  <p className="text-gray-800 font-medium">{ventaja}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TrustedInstitutions />

      {/* HITCH Section */}
      <section className="py-20 bg-gradient-to-br from-notaria-primary to-notaria-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">SISTEMAS DIGITALES + HITCH</h2>
              <div className="flex justify-center mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_public-notary-lab/artifacts/dicik570_ISOTIPO%20COLOR%20HICTH.png" 
                  alt="Hitch" 
                  className="h-24 w-auto"
                />
              </div>
            </div>

            <div className="prose prose-lg max-w-none text-white/90 leading-relaxed mb-12">
              <p className="text-lg mb-6">
                El protocolo <strong>HITChain</strong> establece un marco jurídico-tecnológico para la certificación, custodia y transferencia de documentos digitales tokenizados. Su objetivo es proporcionar <strong>certeza jurídica</strong> a los activos digitales documentales mediante la combinación de la fe pública notarial con la tecnología blockchain.
              </p>
              <p className="text-lg mb-6">
                El sistema opera exclusivamente en <strong>redes principales (mainnet)</strong> — Ethereum y Hedera Hashgraph — utilizando un modelo de <strong>attestation</strong> donde el hash criptográfico del documento se registra directamente en la cadena de bloques sin necesidad de contratos inteligentes específicos por cada documento.
              </p>
              <p className="text-lg mb-6">
                La versión 2.0 introduce <strong>HitchSign</strong>, un protocolo de firma digital interna que permite a firmantes externos (compradores y vendedores) registrarse en la plataforma, firmar digitalmente y generar attestations en blockchain de forma automática.
              </p>
            </div>

            {/* Hitch Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {hitchSteps.map((step, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-lg border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-notaria-primary font-bold">{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-2">{step.title}</h4>
                      <p className="text-white/80">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-lg border border-white/20">
              <p className="text-lg leading-relaxed">
                <strong>HITCH</strong> es una plataforma tecnológica que utiliza blockchain, NFTs y contratos inteligentes para digitalizar, certificar y proteger documentos públicos y privados, garantizando seguridad jurídica, transparencia y automatización en actos notariales, registros de propiedad e identificaciones oficiales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-notaria-primary mb-8 text-center">Hitch + Notaría Pública 90</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              <p className="text-lg mb-6">
                La gestión del talento permite a la notaría mejorar su organización interna, lo que se traduce en un mejor servicio al cliente y cumplimiento más ágil de obligaciones legales.
              </p>
              <p className="text-lg mb-6">
                En nuestra <strong>Notaría</strong>, entendemos que cada cliente y cada trámite requieren un enfoque único, por ello ofrecemos una asesoría <strong>personalizada y adaptada</strong> a cada servicio solicitado. Nuestros sistemas de <strong>Prevención de Lavado de Dinero (PLD)</strong> se ajustan al tipo de operación realizada, garantizando el cumplimiento normativo y brindando seguridad en transacciones patrimoniales, fiscales o corporativas.
              </p>
              <p className="text-lg">
                Con <strong>Hitch</strong> nos permite ajustar la digitalización y certificación de documentos mediante blockchain y contratos inteligentes según el tipo de operación, ya sea un contrato privado o una identificación oficial, asegurando así la máxima protección jurídica en cada caso. Gracias a esta integración, ofrecemos un servicio notarial moderno, eficiente y confiable, donde cada solución se diseña a la medida de las necesidades específicas de nuestro cliente.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicioPLD;
